import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import PageHead from "@/components/feature/PageHead";

const COMPANY_NAME = "NetBasis International";

const title = "Import Management";
const heroImage = "https://net-basis.net/Bilder/import-management-customs-clearance-dach-distribution.webp";
const heroAlt = "Stacked freight containers symbolize import management, customs clearance and local sales.";
const heroClaimLine1 = "More than just";
const heroClaimLine2 = "transport!";
const benefitsTitle = "How you benefit from our __Import Management__ expertise:";
const introText = "Efficient import management for your seamless market access in the DACH region. As local experts, we act as a fully integrated local import department for foreign ICT manufacturers and serve as a strong commercial link to your target markets.";
const p1Text = "NetBasis International serves its foreign manufacturer partners on the ground as their local import department. We handle all essential import activities as well as local sales and marketing efforts within the German-speaking economic area, ensuring our partners benefit directly from our local network and immediate access to the right business partners.";
const p1Parts = p1Text.split(COMPANY_NAME);
const bottomClaim = "NetBasis International - drive to excellence with excellent drivers !";

const benefits = [
  "Complete takeover of the entire import process, including professional customs clearance",
  "Correct classification of products by tariff codes and smooth exchange of information with all trade partners",
  "Reliable assurance of correct country-specific product labeling and packaging",
  "Legally compliant execution of all processes with strict adherence to local prohibitions and restrictions",
  "Comprehensive mitigation and safeguarding against all local financial and legal risks",
  "Flawless and timely logistics and delivery to local dealers as well as distributors",
  "Strategic execution of targeted advertising, marketing, and promotional sales activities",
  "Professional planning, organization, execution of, and participation in relevant trade fairs and congresses",
  "Reliable takeover and handling of all local invoicing, accounting, and financial management",
];

export default function ImportManagementEnPage() {
  return (
    <div className="min-h-screen flex flex-col">
      <PageHead
        title="Import Management | NetBasis International"
        description="Smooth processing, logistical coordination, and compliance with all local guidelines for your international goods flows."
        ogUrl="https://net-basis.net/en/services/business-development/import-management"
      />
      <Header />

      <main className="flex-1 pt-20 md:pt-24">
        {/* Hero image */}
        <section className="w-full bg-white py-4 md:py-6 lg:py-8">
          <div className="w-full max-w-[1100px] mx-auto">
            <div className="relative w-full h-[320px] md:h-[420px] lg:h-[500px] overflow-hidden">
              <img loading="lazy"
                src={heroImage}
                alt={heroAlt}
                title={title}
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>

              <div className="absolute inset-0 z-20 flex items-start justify-start overflow-hidden pointer-events-none pt-10 sm:pt-16 md:pt-[58px] lg:pt-[72px] pl-8 sm:pl-8 md:pl-[62px] lg:pl-14">
                <p
                  className="text-3xl md:text-4xl lg:text-5xl font-normal text-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)] text-left importmanagement-hero-text-slide im-hero-lh-15"
                  style={{ fontFamily: '"Open Sans", sans-serif' }}
                >
                  <span className="block md:inline">{heroClaimLine1}</span>
                  <span className="hidden md:inline">&nbsp;</span>
                  <span className="block md:inline">{heroClaimLine2}</span>
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-6 md:py-8 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-5 md:mb-7">
              {title}
            </h2>

            <h1 className="font-semibold text-foreground-950 leading-relaxed text-sm md:text-base mb-4">
              {introText}
            </h1>

            <div className="space-y-4 text-foreground-950 leading-relaxed text-sm md:text-base">
              <p>
                {p1Parts.map((part, i) => (
                  <span key={i}>
                    {part}
                    {i < p1Parts.length - 1 && (
                      <strong className="text-primary-600">{COMPANY_NAME}</strong>
                    )}
                  </span>
                ))}
              </p>
            </div>

            {/* Green heading with italic word */}
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mt-8 md:mt-10 mb-5 md:mb-7">
              {benefitsTitle.split("__").map((part, i) =>
                i % 2 === 1 ? <em key={i}>{part}</em> : part
              )}
            </h2>

            {/* Benefits with green checkmarks */}
            <div className="space-y-1.5 md:space-y-2 text-foreground-950 leading-relaxed text-sm md:text-base">
              {benefits.map((benefit, idx) => (
                <div key={idx} className="flex items-start gap-2 md:gap-3">
                  <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center mt-0.5">
                    <i className="ri-check-line text-primary-600 text-base md:text-lg"></i>
                  </div>
                  <p>{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Bottom claim */}
        <section className="py-6 md:py-10 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto text-center">
            <p className="font-heading text-lg md:text-xl font-bold text-primary-600">
              {bottomClaim}
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}